import { Dropdown } from '@eventbrite/eds-containers';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import {
    clearNavigatorRecentSearchTerms,
    getNavigatorLastRecentSearchTerms,
} from '@eventbrite/personalization';
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CompletionType,
    DEFAULT_COMPLETION_TYPES,
} from '../../../api/searchAutocomplete';
import SearchQuerySuggestions from '../../../containers/SearchSuggestions';
import { useSearchInputContext } from '../../../context';
import { useSearchAutocompleteQuery } from '../../../hooks';
import { SearchAutocompleteResponse } from '../../../types';
import { AISearchCTA } from './AISearchCTA';
import RecentSearches from './RecentSearches/RecentSearches';
import './SearchDropdown.scss';
import TrendingSearches from './TrendingSearches/TrendingSearches';

export type Props = {
    onClickOutside: (e?: React.MouseEvent<HTMLElement>) => void;
    isAISearchEnabled?: boolean;
    openAISearch: () => void;
    showFullscreenMobileSearch?: boolean;
};

const getDropdownContent = ({
    completionTypes,
    autocompleteData,
    handleClearRecentSearches,
    recentSearches,
    query,
}: {
    completionTypes: CompletionType[];
    autocompleteData?: SearchAutocompleteResponse;
    handleClearRecentSearches: () => void;
    recentSearches: string[];
    query?: string;
}) => {
    const hasAutocompleteData =
        !!autocompleteData?.query.length ||
        !!autocompleteData?.organizer?.length ||
        !!autocompleteData?.venue?.length;

    if (query && hasAutocompleteData) {
        return <SearchQuerySuggestions completionTypes={completionTypes} />;
    } else if (query && !hasAutocompleteData) {
        return null;
    } else if (recentSearches.length > 0) {
        return (
            <RecentSearches
                recentSearches={recentSearches}
                handleClearRecentSearches={handleClearRecentSearches}
            />
        );
    }

    return <TrendingSearches />;
};

const SearchDropdown: React.FunctionComponent<Props> = ({
    onClickOutside,
    isAISearchEnabled,
    openAISearch,
    showFullscreenMobileSearch,
}) => {
    const { completionTypes = DEFAULT_COMPLETION_TYPES } =
        useSearchInputContext();
    const [contentHeight, setContentHeight] = useState<string>('inherit');
    const [recentSearches, setRecentSearches] = useState(
        getNavigatorLastRecentSearchTerms(5),
    );

    const handleClearRecentSearches = React.useCallback(() => {
        clearNavigatorRecentSearchTerms();
        setRecentSearches([]);
    }, [setRecentSearches]);

    const { query } = useSearchInputContext();
    const { data: autocompleteData } = useSearchAutocompleteQuery({
        completionTypes,
    });

    const onResizeHandler = useCallback(() => {
        if (
            !showFullscreenMobileSearch ||
            !HAS_WINDOW ||
            !window.visualViewport
        ) {
            setContentHeight('inherit');
        } else {
            const visualHeight = `calc(${window.visualViewport.height}px - 179px)`;
            setContentHeight(visualHeight);
        }
    }, [showFullscreenMobileSearch]);

    useEffect(() => {
        if (HAS_WINDOW && window.visualViewport) {
            window.visualViewport.addEventListener('resize', onResizeHandler);
        }
        return () => {
            if (HAS_WINDOW && window.visualViewport) {
                window.visualViewport.removeEventListener(
                    'resize',
                    onResizeHandler,
                );
            }
        };
    }, [onResizeHandler]);

    const content = useMemo(
        () =>
            getDropdownContent({
                completionTypes,
                autocompleteData: autocompleteData || undefined,
                handleClearRecentSearches,
                recentSearches,
                query,
            }),
        [
            autocompleteData,
            completionTypes,
            handleClearRecentSearches,
            query,
            recentSearches,
        ],
    );

    if (!content) {
        return null;
    }

    return (
        <Dropdown onClickOutside={onClickOutside}>
            <div
                className={classnames('search-takeover-wrapper', {
                    'search-takeover-wrapper--fullscreen':
                        showFullscreenMobileSearch,
                })}
                data-testid="searchbar-dropdown"
            >
                <AISearchCTA
                    isEnabled={Boolean(isAISearchEnabled)}
                    onClick={openAISearch}
                />
                <div
                    className="search-takeover-content"
                    style={{ height: contentHeight }}
                >
                    {content}
                </div>
            </div>
        </Dropdown>
    );
};

export default SearchDropdown;
